import upperFirst from 'lodash/upperFirst';
import invert from 'lodash/invert';
import { useTranslate } from '@im/react-localization';
import { AlertColor, AlertPropsColorOverrides } from '@mui/material/Alert';
import { EDStatus, ExceptionBidStatus } from '../hooks/exceptionDeal';

type AlertAttrs = {
  alertColor?: AlertColor | keyof AlertPropsColorOverrides;
  alertMessage?: string;
  alertTitle?: string;
  alertType?: AlertColor;
};

export function useStatusAlertAttrs(
  isAdmin: boolean,
  status?: ExceptionBidStatus
): AlertAttrs {
  const translate = useTranslate();

  const getAlertStyle = (
    status: ExceptionBidStatus,
    role: 'admin' | 'other'
  ) => {
    const styleMap = {
      [EDStatus.NEW]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.NEW_ENDUSER]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.NEW_RESELLER]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.IN_PROCESS]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.IN_QUEUE]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.PENDING_REVIEW]: {
        alertType: 'info',
      },
      [EDStatus.REJECTED]: {
        alertColor: 'attention',
        alertType: 'warning',
      },
      [EDStatus.ESCALATED]: {
        alertType: 'error',
      },
      [EDStatus.APPROVED]: {
        alertType: 'success',
      },
      [EDStatus.COMPLETED]: {
        alertType: 'success',
      },
      [EDStatus.DEACTIVATED]: {
        alertType: 'error',
      },
    } as const;
    const roleMap = {
      admin: styleMap,
      other: {
        ...styleMap,
        [EDStatus.PENDING_REVIEW]: {
          alertType: 'success',
        },
        [EDStatus.REJECTED]: {
          alertType: 'warning',
        },
      } as const,
    };
    const map = roleMap[role] || {};
    return Object.prototype.hasOwnProperty.call(map, status)
      ? map[status as keyof typeof map]
      : {};
  };

  const role = isAdmin ? 'admin' : 'other';

  return status
    ? {
        alertMessage: translate(
          `ExceptionDeals.Status${upperFirst(role)}${upperFirst(
            invert(EDStatus)[status]
          )}Description`
        ),
        alertTitle: translate(
          `ExceptionDeals.Status${upperFirst(role)}${upperFirst(
            invert(EDStatus)[status]
          )}Title`
        ),
        ...getAlertStyle(status, role),
      }
    : {};
}
